import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import Image from "../../components/AppImage";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import ImageSourceDropdown from "../customer-survey/components/ImageSourceDropdown";
import {
  customers as customersAPI,
  transportWork as transportWorkAPI,
  staffAssignments as staffAssignmentsAPI,
  notifications as notificationsAPI,
} from "../../lib/database";

const STATUS_ORDER = {
  survey_not_started: 1,
  survey_inprogress: 2,
  survey_complete: 3,
  survey_unworkable: 3,
  transport_pending: 4,
  transport_inprogress: 5,
  transport_complete: 6,
  civil_pending: 7,
  civil_inprogress: 8,
  civil_complete: 9,
  technical_pending: 10,
  technical_inprogress: 11,
  technical_complete: 12,
  commissioning_not_started: 13,
  commissioning_inprogress: 14,
  commissioning_complete: 15,
};

const TransportWorkflow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [customerId, setCustomerId] = useState(null);
  const [mode, setMode] = useState("edit");
  const [customer, setCustomer] = useState(null);
  const [kitPhoto, setKitPhoto] = useState(null); // File | string | null
  const [panelPhoto, setPanelPhoto] = useState(null); // File | string | null
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const saveTimeoutRef = useRef(null);
  const SAVE_DEBOUNCE_MS = 1500;
  const formRef = useRef({ kitPhoto: null, panelPhoto: null });

  useEffect(() => {
    formRef.current = { kitPhoto, panelPhoto };
  }, [kitPhoto, panelPhoto]);

  useEffect(() => {
    const id = searchParams.get("customerId");
    const nextMode = searchParams.get("mode");
    if (id) setCustomerId(id);
    if (nextMode === "view" || nextMode === "edit") setMode(nextMode);
  }, [searchParams]);

  useEffect(() => {
    const load = async () => {
      if (!customerId) return;
      try {
        const c = await customersAPI.getById(customerId);
        setCustomer(c);
        if (
          c &&
          user?.role === "worker" &&
          (STATUS_ORDER[c.status] || 0) < (STATUS_ORDER.transport_inprogress || 0)
        ) {
          await customersAPI.update(customerId, {
            status: "transport_inprogress",
          });
          setCustomer((prev) =>
            prev ? { ...prev, status: "transport_inprogress" } : prev,
          );
        }
        const existingTransport =
          await transportWorkAPI.getByCustomerId(customerId);
        if (existingTransport?.customer_kit_photo) {
          setKitPhoto(existingTransport.customer_kit_photo);
        }
        if (existingTransport?.panel_structure_photo) {
          setPanelPhoto(existingTransport.panel_structure_photo);
        }
      } catch (e) {
        console.error(e);
        showToast("Failed to load customer.", "error");
      }
    };
    if (user) load();
  }, [customerId, user, showToast]);

  const isEditable = mode !== "view" && !!customer;
  const getPhotoSrc = (photo) =>
    photo instanceof File ? URL.createObjectURL(photo) : photo;

  const handleAutoSave = useCallback(
    async ({ silent = false } = {}) => {
      if (!isEditable || !customerId) return;
      const current = formRef.current;
      setIsSaving(true);
      try {
        const kitUrl =
          current.kitPhoto instanceof File
            ? await transportWorkAPI.uploadPhoto(
                customerId,
                current.kitPhoto,
                "customer-kit",
              )
            : current.kitPhoto;
        const panelUrl =
          current.panelPhoto instanceof File
            ? await transportWorkAPI.uploadPhoto(
                customerId,
                current.panelPhoto,
                "panel-structure",
              )
            : current.panelPhoto;

        await transportWorkAPI.upsertForCustomer(customerId, {
          customer_kit_photo: kitUrl || null,
          panel_structure_photo: panelUrl || null,
        });

        if (kitUrl !== current.kitPhoto) setKitPhoto(kitUrl);
        if (panelUrl !== current.panelPhoto) setPanelPhoto(panelUrl);
        setLastSavedAt(new Date());
        if (!silent) showToast("Progress saved.", "success");
      } catch (err) {
        console.error(err);
        if (!silent) showToast("Failed to save progress.", "error");
      } finally {
        setIsSaving(false);
      }
    },
    [customerId, isEditable, showToast],
  );

  const scheduleSave = useCallback(() => {
    if (!isEditable || !customerId) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      handleAutoSave({ silent: true }).catch(() => {});
    }, SAVE_DEBOUNCE_MS);
  }, [customerId, isEditable, handleAutoSave]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const handleSubmit = async () => {
    if (!isEditable) {
      showToast("Transport is view-only for this status.", "info");
      return;
    }
    if (!customerId) {
      showToast("Missing customer.", "error");
      return;
    }
    setSubmitting(true);
    try {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      const kitUrl =
        kitPhoto instanceof File
          ? await transportWorkAPI.uploadPhoto(
              customerId,
              kitPhoto,
              "customer-kit",
            )
          : kitPhoto;
      const panelUrl =
        panelPhoto instanceof File
          ? await transportWorkAPI.uploadPhoto(
              customerId,
              panelPhoto,
              "panel-structure",
            )
          : panelPhoto;

      await transportWorkAPI.upsertForCustomer(customerId, {
        customer_kit_photo: kitUrl,
        panel_structure_photo: panelUrl,
        submitted_by: user?.id || null,
        approval_status: "pending",
        approval_decision_by: null,
        approval_decision_at: null,
        disapproval_reason: null,
      });

      if (
        (STATUS_ORDER.transport_complete || 0) >
        (STATUS_ORDER[customer?.status] || 0)
      ) {
        await customersAPI.update(customerId, { status: "transport_complete" });
      }

      try {
        const existingAssignment =
          await staffAssignmentsAPI.getByCustomerAndUser(customerId, user.id);
        if (existingAssignment) {
          await staffAssignmentsAPI.markCompleted(customerId, user.id);
        } else {
          await staffAssignmentsAPI.create(customerId, user.id);
          await staffAssignmentsAPI.markCompleted(customerId, user.id);
        }
      } catch (assignmentError) {
        console.error("Error updating assignment:", assignmentError);
      }

      try {
        await notificationsAPI.notifyClusterManagerOnWorkCompletion(
          customerId,
          "transport",
        );
      } catch (notifError) {
        console.error(notifError);
      }

      showToast("Transport documentation submitted for approval.", "success");
      navigate("/worker-dashboard");
    } catch (err) {
      console.error(err);
      showToast(err?.message || "Failed to submit.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!customerId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Missing customer.</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Transport — Upload Photos</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <main className="content-area">
          <div className="max-w-2xl mx-auto px-4 md:px-6 py-8">
            <div className="mb-6">
              <Button
                variant="ghost"
                size="sm"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate("/worker-dashboard")}
              >
                Back
              </Button>
            </div>
            <h1 className="text-2xl font-semibold text-foreground mb-2">
              Transport photos
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {customer?.name ? (
                <>
                  Customer:{" "}
                  <span className="text-foreground">{customer.name}</span>
                  {customer.cin ? (
                    <span className="font-mono ml-2">({customer.cin})</span>
                  ) : null}
                </>
              ) : (
                "Loading…"
              )}
            </p>

            <div className="bg-card border border-border rounded-lg p-6 space-y-6">
              <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/30">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">
                    Customer &amp; kit photo
                  </label>
                  {kitPhoto ? (
                    <span className="text-xs text-success font-medium">
                      1/1 selected
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      0/1 selected
                    </span>
                  )}
                </div>
                {!kitPhoto && isEditable && (
                  <ImageSourceDropdown
                    idPrefix="transport-customer-kit"
                    triggerLabel="Add Customer & Kit Photo"
                    triggerSubLabel="Upload or capture"
                    onFileSelect={(file) => {
                      setKitPhoto(file);
                      scheduleSave();
                    }}
                  />
                )}
                {kitPhoto && (
                  <div className="relative rounded-lg overflow-hidden border border-border bg-muted">
                    <div className="aspect-[4/3] overflow-hidden">
                      <Image
                        src={getPhotoSrc(kitPhoto)}
                        alt="Customer and kit"
                        className="w-full h-full object-contain bg-muted"
                      />
                    </div>
                    {isEditable && (
                      <div className="absolute top-2 right-2">
                        <Button
                          variant="destructive"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => {
                            setKitPhoto(null);
                            scheduleSave();
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/30">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">
                    Panel structure photo
                  </label>
                  {panelPhoto ? (
                    <span className="text-xs text-success font-medium">
                      1/1 selected
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      0/1 selected
                    </span>
                  )}
                </div>
                {!panelPhoto && isEditable && (
                  <ImageSourceDropdown
                    idPrefix="transport-panel-structure"
                    triggerLabel="Add Panel Structure Photo"
                    triggerSubLabel="Upload or capture"
                    onFileSelect={(file) => {
                      setPanelPhoto(file);
                      scheduleSave();
                    }}
                  />
                )}
                {panelPhoto && (
                  <div className="relative rounded-lg overflow-hidden border border-border bg-muted">
                    <div className="aspect-[4/3] overflow-hidden">
                      <Image
                        src={getPhotoSrc(panelPhoto)}
                        alt="Panel structure"
                        className="w-full h-full object-contain bg-muted"
                      />
                    </div>
                    {isEditable && (
                      <div className="absolute top-2 right-2">
                        <Button
                          variant="destructive"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => {
                            setPanelPhoto(null);
                            scheduleSave();
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <div className="mr-auto self-center text-xs text-muted-foreground">
                  {isSaving ? (
                    <span className="text-primary/80 animate-pulse">Saving...</span>
                  ) : lastSavedAt ? (
                    <span className="text-success">Data saved</span>
                  ) : null}
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate("/worker-dashboard")}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                {isEditable ? (
                  <Button
                    variant="default"
                    iconName="CheckCircle2"
                    iconPosition="left"
                    onClick={handleSubmit}
                    loading={submitting}
                    disabled={submitting}
                  >
                    Submit
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="mt-6 flex items-start gap-2 text-sm text-muted-foreground">
              <Icon name="Info" size={18} className="shrink-0 mt-0.5" />
              <span>
                After submission, your cluster manager can approve transport and
                assign civil work.
              </span>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default TransportWorkflow;
